import React, { useState, useEffect, useCallback, useRef } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import { COLOR_CONDICION_EPP, CONDICIONES_EPP, ETIQUETA_CONDICION_EPP, type CondicionEpp } from '../lib/epp-condiciones';
import RegistroFirmasModal from '../components/firmas/RegistroFirmasModal';
import RegistrosConservados from '../components/firmas/RegistrosConservados';
import { aFechaLocal, hoyEc, sumarMesesDia } from '../lib/fechas';
import { cargarFirmas, contenidoDeEntrega, firmasDisponibles, type DatosFirmas } from '../lib/firmas/firmas-datos';
import { estadoVisible, faltantesContenido } from '../lib/firmas/firmas-logica';
import MatrizEppPuesto from '../components/epp/MatrizEppPuesto';

// ── Types ──────────────────────────────────────────────────────────────────────

export type EppCategory =
  | 'cabeza' | 'ojos_cara' | 'respiratorio' | 'manos'
  | 'pies' | 'cuerpo' | 'caidas' | 'auditivo' | 'otro';

// Solo los valores que acepta la base (lib/epp-condiciones.ts); la opción anterior para el EPP gastado no existía en la base.
export type AssignmentCondition = CondicionEpp;

export interface EppItem {
  id: string;
  company_id: string;
  name: string;
  category: EppCategory;
  brand: string | null;
  model: string | null;
  lifespan_months: number | null;
  stock_total: number;
  notes: string | null;
  created_at: string;
}

export interface EppAssignment {
  id: string;
  company_id: string;
  worker_id: string;
  epp_id: string;
  assigned_date: string;
  expiry_date: string | null;
  condition: AssignmentCondition;
  returned: boolean;
  returned_date: string | null;
  notes: string | null;
  created_at: string;
}

interface Worker { id: string; full_name: string; cedula: string | null; }

// ── Label & color maps ─────────────────────────────────────────────────────────

export const EPP_CATEGORY_LABELS: Record<EppCategory, string> = {
  cabeza:       'Cabeza (casco)',
  ojos_cara:    'Ojos y cara',
  respiratorio: 'Respiratorio',
  manos:        'Manos (guantes)',
  pies:         'Pies (calzado)',
  cuerpo:       'Cuerpo / ropa',
  caidas:       'Protección caídas',
  auditivo:     'Auditivo',
  otro:         'Otro',
};

const EPP_CATEGORY_COLORS: Record<EppCategory, { bg: string; color: string }> = {
  cabeza:       { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  ojos_cara:    { bg: 'rgba(59,130,246,0.12)',   color: '#3b82f6' },
  respiratorio: { bg: 'rgba(6,182,212,0.12)',    color: '#06b6d4' },
  manos:        { bg: 'rgba(34,197,94,0.12)',    color: '#22c55e' },
  pies:         { bg: 'rgba(139,92,246,0.12)',   color: '#8b5cf6' },
  cuerpo:       { bg: 'rgba(236,72,153,0.12)',   color: '#ec4899' },
  caidas:       { bg: 'rgba(239,68,68,0.12)',    color: '#ef4444' },
  auditivo:     { bg: 'rgba(251,191,36,0.12)',   color: '#fbbf24' },
  otro:         { bg: 'rgba(100,116,139,0.12)',  color: '#64748b' },
};

const CONDITION_LABELS: Record<AssignmentCondition, string> = ETIQUETA_CONDICION_EPP;
const CONDITION_COLORS: Record<AssignmentCondition, string> = COLOR_CONDICION_EPP;

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(d: string | null): string {
  if (!d) return '—';
  // Fechas sin hora (assigned_date, expiry_date, returned_date) como locales: con new Date salían un día antes.
  return aFechaLocal(d).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function expiryStatus(expiry: string | null): 'ok' | 'warning' | 'expired' | null {
  if (!expiry) return null;
  const d = new Date(expiry);
  const now = new Date();
  if (d < now) return 'expired';
  const in30 = new Date(); in30.setDate(now.getDate() + 30);
  if (d <= in30) return 'warning';
  return 'ok';
}

// Vencimiento de una entrega: aritmética de calendario sobre la fecha (sin horas ni zonas). Antes,
// new Date('2026-03-31') + setMonth en la hora local daba 2026-05-01 en Ecuador.
function addMonths(dateStr: string, months: number): string {
  return sumarMesesDia(dateStr, months);
}

// ── Category Badge ─────────────────────────────────────────────────────────────

function CategoryBadge({ cat }: { cat: EppCategory }) {
  const { bg, color } = EPP_CATEGORY_COLORS[cat];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: bg, color, fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {EPP_CATEGORY_LABELS[cat]}
    </span>
  );
}

// ── EPP Catalog Modal ──────────────────────────────────────────────────────────

interface CatalogModalProps {
  item: Partial<EppItem> | null;
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}

function CatalogModal({ item, companyId, onClose, onSaved }: CatalogModalProps) {
  const isEdit = !!item?.id;
  const [name,           setName]           = useState(item?.name ?? '');
  const [category,       setCategory]       = useState<EppCategory>(item?.category ?? 'cabeza');
  const [brand,          setBrand]          = useState(item?.brand ?? '');
  const [model,          setModel]          = useState(item?.model ?? '');
  const [lifespan,       setLifespan]       = useState(item?.lifespan_months != null ? String(item.lifespan_months) : '');
  const [stockTotal,     setStockTotal]     = useState(item?.stock_total != null ? String(item.stock_total) : '0');
  const [notes,          setNotes]          = useState(item?.notes ?? '');
  const [errors,         setErrors]         = useState<Record<string, string>>({});
  const [saving,         setSaving]         = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'El nombre es requerido';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    const payload = {
      company_id:       companyId,
      name:             name.trim(),
      category,
      brand:            brand.trim() || null,
      model:            model.trim() || null,
      lifespan_months:  lifespan ? Number(lifespan) : null,
      stock_total:      Number(stockTotal) || 0,
      notes:            notes.trim() || null,
    };
    const { error } = isEdit
      ? await supabase.from('epp_catalog').update(payload).eq('id', item!.id!)
      : await supabase.from('epp_catalog').insert(payload);
    setSaving(false);
    if (error) { setErrors({ general: error.message }); return; }
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 520, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>{isEdit ? 'Editar EPP' : 'Agregar EPP al catálogo'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {errors.general && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: '0.85rem' }}>{errors.general}</div>}
          <FieldRow label="Nombre *" error={errors.name}>
            <input value={name} onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }} placeholder="Casco de seguridad industrial" style={inputStyle} />
          </FieldRow>
          <FieldRow label="Categoría *">
            <select value={category} onChange={e => setCategory(e.target.value as EppCategory)} style={inputStyle}>
              {(Object.keys(EPP_CATEGORY_LABELS) as EppCategory[]).map(k => (
                <option key={k} value={k}>{EPP_CATEGORY_LABELS[k]}</option>
              ))}
            </select>
          </FieldRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FieldRow label="Marca">
              <input value={brand} onChange={e => setBrand(e.target.value)} placeholder="3M, MSA, Honeywell…" style={inputStyle} />
            </FieldRow>
            <FieldRow label="Modelo">
              <input value={model} onChange={e => setModel(e.target.value)} placeholder="V-Gard 500" style={inputStyle} />
            </FieldRow>
          </div>
          <FieldRow label="Vida útil (meses)" error={errors.lifespan}>
            <input type="number" value={lifespan} onChange={e => setLifespan(e.target.value)} placeholder="12" min={1} style={inputStyle} />
          </FieldRow>
          <FieldRow label="Stock total *">
            <input type="number" value={stockTotal} onChange={e => setStockTotal(e.target.value)} placeholder="Ej: 50" min={0} style={inputStyle} />
          </FieldRow>
          <FieldRow label="Notas">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Observaciones, normas aplicables…" style={{ ...inputStyle, resize: 'vertical' }} />
          </FieldRow>
        </div>
        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.7 : 1 }}>{saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Agregar EPP'}</button>
        </div>
      </div>
    </div>
  );
}

// ── EPP Assignment Modal ───────────────────────────────────────────────────────

interface AssignModalProps {
  assignment: Partial<EppAssignment> | null;
  workers: Worker[];
  catalog: EppItem[];
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}

function AssignModal({ assignment, workers, catalog, companyId, onClose, onSaved }: AssignModalProps) {
  const isEdit = !!assignment?.id;

  const [workerId,      setWorkerId]      = useState(assignment?.worker_id ?? '');
  const [eppId,         setEppId]         = useState(assignment?.epp_id ?? '');
  const [assignedDate,  setAssignedDate]  = useState(assignment?.assigned_date ?? hoyEc());
  const [expiryDate,    setExpiryDate]    = useState(assignment?.expiry_date ?? '');
  const [condition,     setCondition]     = useState<AssignmentCondition>(assignment?.condition ?? 'nuevo');
  const [notes,         setNotes]         = useState(assignment?.notes ?? '');
  const [errors,        setErrors]        = useState<Record<string, string>>({});
  const [saving,        setSaving]        = useState(false);
  const [workerSearch,  setWorkerSearch]  = useState('');
  const [showWorkers,   setShowWorkers]   = useState(false);
  const workerRef = useRef<HTMLDivElement>(null);

  // Pre-fill worker name when editing
  const selectedWorker = workers.find(w => w.id === workerId);
  const selectedEpp    = catalog.find(e => e.id === eppId);

  // Auto-calculate expiry when EPP or assigned date changes
  useEffect(() => {
    if (eppId && assignedDate) {
      const epp = catalog.find(e => e.id === eppId);
      if (epp?.lifespan_months) {
        setExpiryDate(addMonths(assignedDate, epp.lifespan_months));
      }
    }
  }, [eppId, assignedDate, catalog]);

  // Close worker dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (workerRef.current && !workerRef.current.contains(e.target as Node)) setShowWorkers(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredWorkers = workers.filter(w => {
    const q = workerSearch.toLowerCase();
    return !q || w.full_name.toLowerCase().includes(q) || (w.cedula ?? '').includes(q);
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (!workerId) e.worker = 'Selecciona un trabajador';
    if (!eppId) e.epp = 'Selecciona un EPP';
    if (!assignedDate) e.assignedDate = 'La fecha de asignación es requerida';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    const payload = {
      company_id:    companyId,
      worker_id:     workerId,
      epp_id:        eppId,
      assigned_date: assignedDate,
      expiry_date:   expiryDate || null,
      condition,
      notes:         notes.trim() || null,
    };
    const { error } = isEdit
      ? await supabase.from('epp_assignments').update(payload).eq('id', assignment!.id!)
      : await supabase.from('epp_assignments').insert({ ...payload, returned: false });
    setSaving(false);
    if (error) { setErrors({ general: error.message }); return; }
    onSaved();
  };

  const handleReturn = async () => {
    if (!assignment?.id) return;
    setSaving(true);
    const { error } = await supabase.from('epp_assignments').update({
      returned: true,
      returned_date: hoyEc(),
    }).eq('id', assignment.id);
    if (error) {
      alert('Error al registrar devolución: ' + error.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 540, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>{isEdit ? 'Editar asignación' : 'Nueva asignación de EPP'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {errors.general && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: '0.85rem' }}>{errors.general}</div>}

          {/* Worker searchable select */}
          <FieldRow label="Trabajador *" error={errors.worker}>
            <div ref={workerRef} style={{ position: 'relative' }}>
              <input
                value={showWorkers ? workerSearch : (selectedWorker ? selectedWorker.full_name : '')}
                onChange={e => { setWorkerSearch(e.target.value); setShowWorkers(true); }}
                onFocus={() => { setWorkerSearch(''); setShowWorkers(true); }}
                placeholder="Buscar trabajador…"
                style={{ ...inputStyle, cursor: 'pointer' }}
                readOnly={!showWorkers && !!selectedWorker}
              />
              {showWorkers && filteredWorkers.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, zIndex: 50, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {filteredWorkers.slice(0, 20).map(w => (
                    <button
                      key={w.id}
                      onClick={() => { setWorkerId(w.id); setWorkerSearch(''); setShowWorkers(false); setErrors(p => ({ ...p, worker: '' })); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', color: '#e2e8f0', cursor: 'pointer', fontSize: '0.86rem', borderBottom: '1px solid #1e293b' }}
                    >
                      {w.full_name}
                      {w.cedula && <span style={{ color: '#64748b', marginLeft: 8, fontSize: '0.78rem' }}>{w.cedula}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FieldRow>

          {/* EPP select */}
          <FieldRow label="EPP *" error={errors.epp}>
            <select value={eppId} onChange={e => { setEppId(e.target.value); setErrors(p => ({ ...p, epp: '' })); }} style={inputStyle}>
              <option value="">Seleccionar EPP del catálogo…</option>
              {catalog.map(e => (
                <option key={e.id} value={e.id}>{e.name}{e.brand ? ` — ${e.brand}` : ''}{e.model ? ` ${e.model}` : ''}</option>
              ))}
            </select>
          </FieldRow>
          {selectedEpp?.lifespan_months && (
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: -8 }}>Vida útil: {selectedEpp.lifespan_months} meses</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FieldRow label="Fecha asignación *" error={errors.assignedDate}>
              <input type="date" value={assignedDate} onChange={e => setAssignedDate(e.target.value)} style={inputStyle} />
            </FieldRow>
            <FieldRow label="Fecha vencimiento">
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} style={inputStyle} />
            </FieldRow>
          </div>

          <FieldRow label="Condición">
            <select value={condition} onChange={e => setCondition(e.target.value as AssignmentCondition)} style={inputStyle}>
              {CONDICIONES_EPP.map(k => (
                <option key={k} value={k}>{CONDITION_LABELS[k]}</option>
              ))}
            </select>
          </FieldRow>

          <FieldRow label="Notas">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Observaciones…" style={{ ...inputStyle, resize: 'vertical' }} />
          </FieldRow>
        </div>
        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {isEdit && !assignment?.returned && (
              <button onClick={handleReturn} disabled={saving} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem' }}>
                Marcar como devuelto
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={cancelBtnStyle}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.7 : 1 }}>{saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Asignar EPP'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Field helper ───────────────────────────────────────────────────────────────

function FieldRow({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600 }}>{label}</label>
      {children}
      {error && <span style={{ fontSize: '0.76rem', color: '#ef4444' }}>{error}</span>}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
  color: '#e2e8f0', padding: '10px 12px', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
  border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '9px 18px', background: 'none', border: '1px solid #334155',
  color: '#64748b', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem',
};

// ── Mini cards ─────────────────────────────────────────────────────────────────

function MiniCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value}</span>
      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{label}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function EPPPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';
  const canEdit = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const isAdmin = profile?.role === 'admin' || !!impersonated;

  // Registro de entrega-recepción firmado por el trabajador (PO 22)
  const [firmasOk,    setFirmasOk]    = useState(false);
  const [datosFirmas, setDatosFirmas] = useState<DatosFirmas>({ registros: [], firmas: [], itemsEpp: [] });
  const [firmaDe,     setFirmaDe]     = useState<EppAssignment | null>(null);

  const [tab, setTab] = useState<'catalogo' | 'asignaciones' | 'matriz'>('catalogo');

  // Catalog
  const [catalog,  setCatalog]  = useState<EppItem[]>([]);
  const [filterCat, setFilterCat] = useState('');
  const [catalogModal, setCatalogModal] = useState<Partial<EppItem> | null | undefined>(undefined);

  // Assignments
  const [assignments, setAssignments] = useState<EppAssignment[]>([]);
  const [workers,     setWorkers]     = useState<Worker[]>([]);
  const [filterWorker, setFilterWorker] = useState('');
  const [filterACat,   setFilterACat]   = useState('');
  const [filterStatus, setFilterStatus] = useState<'' | 'vigente' | 'vencido' | 'devuelto'>('');
  const [assignModal, setAssignModal]   = useState<Partial<EppAssignment> | null | undefined>(undefined);

  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: cat }, { data: asgn }, { data: wks }] = await Promise.all([
      supabase.from('epp_catalog').select('*').eq('company_id', companyId).order('name'),
      supabase.from('epp_assignments').select('*').eq('company_id', companyId).order('assigned_date', { ascending: false }),
      supabase.from('workers').select('id, full_name, cedula').eq('company_id', companyId).eq('status', 'activo').order('full_name'),
    ]);
    setCatalog((cat ?? []) as EppItem[]);
    setAssignments((asgn ?? []) as EppAssignment[]);
    setWorkers((wks ?? []) as Worker[]);
    const conFirmas = await firmasDisponibles(companyId);
    setFirmasOk(conFirmas);
    if (conFirmas) setDatosFirmas(await cargarFirmas(companyId, 'epp_entrega').catch(() => ({ registros: [], firmas: [], itemsEpp: [] })));
    setLoading(false);
  }, [companyId]);

  // Registro que contiene la asignación (si hay) y el grupo que se firma junto: el mismo trabajador y la misma fecha.
  const registroDeAsignacion = (id: string) => {
    const item = datosFirmas.itemsEpp.find(i => i.epp_assignment_id === id);
    return item ? datosFirmas.registros.find(r => r.id === item.record_id) ?? null : null;
  };
  const grupoDeEntrega = (a: EppAssignment): EppAssignment[] => {
    const reg = registroDeAsignacion(a.id);
    const enRegistro = new Set(datosFirmas.itemsEpp.filter(i => i.record_id === reg?.id).map(i => i.epp_assignment_id));
    const libres = new Set(datosFirmas.itemsEpp.map(i => i.epp_assignment_id));
    return assignments.filter(x => enRegistro.has(x.id)
      || ((!reg || reg.status === 'abierto') && !libres.has(x.id) && x.worker_id === a.worker_id && x.assigned_date === a.assigned_date));
  };

  useEffect(() => { loadData(); }, [loadData]);

  function handleSaved() { loadData(); setCatalogModal(undefined); setAssignModal(undefined); }

  // ── Catalog filtering ──────────────────────────────────────────────────────

  const filteredCatalog = catalog.filter(e => !filterCat || e.category === filterCat);

  // ── Assignments filtering ──────────────────────────────────────────────────

  const today = new Date();
  const in30  = new Date(); in30.setDate(today.getDate() + 30);

  function getEPPStats(eppId: string) {
    let asignados = 0, vencidos = 0, devueltos = 0;
    for (const a of assignments) {
      if (a.epp_id !== eppId) continue;
      if (a.returned) {
        devueltos++;
      } else if (a.expiry_date && new Date(a.expiry_date) < today) {
        vencidos++;
      } else {
        asignados++;
      }
    }
    const epp = catalog.find(c => c.id === eppId);
    const disponible = Math.max(0, (epp?.stock_total ?? 0) - asignados);
    const stockMinimo = Math.ceil(workers.length * 0.25);
    return { asignados, vencidos, devueltos, total: asignados + vencidos + devueltos, disponible, stockTotal: epp?.stock_total ?? 0, stockMinimo };
  }

  const filteredAssignments = assignments.filter(a => {
    if (filterWorker && a.worker_id !== filterWorker) return false;
    if (filterACat) {
      const epp = catalog.find(e => e.id === a.epp_id);
      if (!epp || epp.category !== filterACat) return false;
    }
    if (filterStatus) {
      if (filterStatus === 'devuelto' && !a.returned) return false;
      if (filterStatus !== 'devuelto' && a.returned) return false;
      if (filterStatus === 'vencido' && (!a.expiry_date || new Date(a.expiry_date) >= today)) return false;
      if (filterStatus === 'vigente') {
        if (a.returned) return false;
        if (a.expiry_date && new Date(a.expiry_date) < today) return false;
      }
    }
    return true;
  });

  // ── Assignment summary metrics ─────────────────────────────────────────────

  const vigentes   = assignments.filter(a => !a.returned && (!a.expiry_date || new Date(a.expiry_date) >= today)).length;
  const vencidos   = assignments.filter(a => !a.returned && a.expiry_date && new Date(a.expiry_date) < today).length;
  const porVencer  = assignments.filter(a => !a.returned && a.expiry_date && new Date(a.expiry_date) >= today && new Date(a.expiry_date) <= in30).length;
  const devueltos  = assignments.filter(a => a.returned).length;

  const workerMap = new Map(workers.map(w => [w.id, w]));
  const eppMap    = new Map(catalog.map(e => [e.id, e]));

  // ── Delete EPP item ────────────────────────────────────────────────────────

  const handleDeleteEpp = async (id: string) => {
    if (!window.confirm('¿Eliminar este EPP del catálogo?')) return;
    const { error } = await supabase.from('epp_catalog').delete().eq('id', id);
    if (error) {
      alert('Error al eliminar EPP: ' + error.message);
      return;
    }
    loadData();
  };

  // ── Plan gate (after all hooks) ──────────────────────────────────────────────

  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin && displayCompany && !hasPlanAccess(displayCompany.plan, 'epp')) {
    return <UpgradeWall {...PLAN_FEATURES.epp} />;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#e2e8f0' }}>Equipos de Protección Personal</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Catálogo de EPP y asignaciones por trabajador — DE 255</p>
          <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
            Stock mínimo calculado automáticamente: 25% de trabajadores activos ({workers.length} trabajadores → mínimo {Math.ceil(workers.length * 0.25)} uds. por ítem)
          </p>
        </div>
        {canEdit && tab !== 'matriz' && (
          <button
            onClick={() => {
              if (tab === 'catalogo') setCatalogModal({});
              else setAssignModal({});
            }}
            style={primaryBtnStyle}
          >
            + {tab === 'catalogo' ? 'Agregar EPP' : 'Nueva asignación'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #334155', overflowX: 'auto' }}>
        {(['catalogo', 'asignaciones', 'matriz'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{ padding: '10px 22px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === t ? '#2FBF71' : 'transparent'}`, color: tab === t ? '#2FBF71' : '#64748b', fontWeight: tab === t ? 700 : 500, fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.2s' }}
          >
            {t === 'catalogo' ? 'Catálogo' : t === 'asignaciones' ? 'Asignaciones' : 'Matriz por puesto'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140 }}>
          <span style={{ color: '#64748b' }}>Cargando…</span>
        </div>
      ) : tab === 'matriz' ? (
        /* ── Matriz por puesto y procedimiento de adquisición (PO 21, M51) ──── */
        <MatrizEppPuesto companyId={companyId} empresa={displayCompany?.name ?? ''} puedeEditar={canEdit} catalogo={catalog} />
      ) : tab === 'catalogo' ? (
        /* ── Catalog Tab ────────────────────────────────────────────────────── */
        <>
          {/* Filter */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
              <option value="">Todas las categorías</option>
              {(Object.keys(EPP_CATEGORY_LABELS) as EppCategory[]).map(k => (
                <option key={k} value={k}>{EPP_CATEGORY_LABELS[k]}</option>
              ))}
            </select>
          </div>

          {filteredCatalog.length === 0 ? (
            <EmptyState msg={catalog.length === 0 ? 'No hay EPP en el catálogo. Agrega el primero.' : 'No hay resultados con los filtros aplicados.'} />
          ) : (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a' }}>
                    {['Nombre', 'Categoría', 'Marca / Modelo', 'Vida útil', 'Disponible', 'Asignados', 'Vencidos', 'Devueltos', 'Total usado', 'Acciones'].map(h => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCatalog.map((e, i) => (
                    <tr key={e.id} style={{ borderTop: '1px solid #334155', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      <td style={{ padding: '12px 16px', color: '#e2e8f0', fontWeight: 600 }}>{e.name}</td>
                      <td style={{ padding: '12px 16px' }}><CategoryBadge cat={e.category} /></td>
                      <td style={{ padding: '12px 16px', color: '#94a3b8' }}>{[e.brand, e.model].filter(Boolean).join(' ') || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#94a3b8' }}>{e.lifespan_months ? `${e.lifespan_months} meses` : '—'}</td>
                      {(() => {
                        const s = getEPPStats(e.id);
                        return (
                          <>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 700, color: s.disponible === 0 ? '#ef4444' : '#22c55e' }}>{s.disponible} / {s.stockTotal}</span>
                                {s.disponible === 0 ? (
                                  <span style={{ padding: '2px 9px', borderRadius: 20, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Sin stock</span>
                                ) : s.disponible < s.stockMinimo ? (
                                  <span style={{ padding: '2px 9px', borderRadius: 20, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Stock bajo — mínimo recomendado: {s.stockMinimo} uds.</span>
                                ) : null}
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 700, color: '#3b82f6' }}>{s.asignados}</span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 700, color: s.vencidos > 0 ? '#ef4444' : '#475569' }}>{s.vencidos}</span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#64748b' }}>{s.devueltos}</span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{s.total}</span>
                            </td>
                          </>
                        );
                      })()}
                      <td style={{ padding: '12px 16px' }}>
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setCatalogModal(e)} style={{ padding: '5px 12px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#3b82f6', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Editar</button>
                            <button onClick={() => handleDeleteEpp(e.id)} style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Eliminar</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        /* ── Assignments Tab ────────────────────────────────────────────────── */
        <>
          {/* Mini cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
            <MiniCard label="Asignaciones vigentes" value={vigentes}  color="#22c55e" />
            <MiniCard label="Vencidas"               value={vencidos}  color="#ef4444" />
            <MiniCard label="Por vencer (30 días)"   value={porVencer} color="#f59e0b" />
            <MiniCard label="Devueltos"               value={devueltos} color="#64748b" />
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '0 1 260px', minWidth: 200 }}>
              <SelectBuscable value={filterWorker} onChange={setFilterWorker} inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Filtrar por trabajador"
                vacio="Todos los trabajadores" opciones={workers.map(w => ({ value: w.id, label: w.full_name }))} />
            </div>
            <select value={filterACat} onChange={e => setFilterACat(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
              <option value="">Todas las categorías</option>
              {(Object.keys(EPP_CATEGORY_LABELS) as EppCategory[]).map(k => (
                <option key={k} value={k}>{EPP_CATEGORY_LABELS[k]}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as typeof filterStatus)} style={{ ...inputStyle, width: 'auto', minWidth: 140 }}>
              <option value="">Todos los estados</option>
              <option value="vigente">Vigente</option>
              <option value="vencido">Vencido</option>
              <option value="devuelto">Devuelto</option>
            </select>
          </div>

          {filteredAssignments.length === 0 ? (
            <EmptyState msg={assignments.length === 0 ? 'No hay asignaciones registradas.' : 'No hay resultados con los filtros aplicados.'} />
          ) : (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a' }}>
                    {['Trabajador', 'EPP', 'Asignado', 'Vencimiento', 'Condición', 'Estado', 'Acciones'].map(h => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map((a, i) => {
                    const worker  = workerMap.get(a.worker_id);
                    const epp     = eppMap.get(a.epp_id);
                    const expSt   = a.returned ? null : expiryStatus(a.expiry_date);
                    return (
                      <tr key={a.id} style={{ borderTop: '1px solid #334155', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={{ padding: '11px 14px', color: '#e2e8f0', fontWeight: 600, maxWidth: 160 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{worker?.full_name ?? '—'}</div>
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          {epp ? (
                            <div>
                              <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{epp.name}</div>
                              <CategoryBadge cat={epp.category} />
                            </div>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(a.assigned_date)}</td>
                        <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                          {a.expiry_date ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                              background: expSt === 'expired' ? 'rgba(239,68,68,0.12)' : expSt === 'warning' ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.08)',
                              color: expSt === 'expired' ? '#ef4444' : expSt === 'warning' ? '#f59e0b' : '#94a3b8',
                            }}>
                              {fmtDate(a.expiry_date)}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600, background: `${CONDITION_COLORS[a.condition] ?? '#64748b'}18`, color: CONDITION_COLORS[a.condition] ?? '#64748b' }}>
                            {CONDITION_LABELS[a.condition] ?? a.condition}
                          </span>
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          {a.returned ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: '#64748b' }}>✓ Devuelto</span>
                          ) : expSt === 'expired' ? (
                            <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>Vencido</span>
                          ) : expSt === 'warning' ? (
                            <span style={{ fontSize: '0.78rem', color: '#f59e0b', fontWeight: 600 }}>Por vencer</span>
                          ) : (
                            <span style={{ fontSize: '0.78rem', color: '#22c55e', fontWeight: 600 }}>Vigente</span>
                          )}
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {canEdit && (
                            <button onClick={() => setAssignModal(a)} style={{ padding: '5px 12px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#3b82f6', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Editar</button>
                          )}
                          {firmasOk && (() => {
                            const reg = registroDeAsignacion(a.id);
                            const est = estadoVisible(reg, reg ? datosFirmas.firmas.filter(f => f.record_id === reg.id) : []);
                            const color = est.estado === 'firmado' ? '#2FBF71' : est.estado === 'en_firma' ? '#f59e0b' : '#94a3b8';
                            return (
                              <button onClick={() => setFirmaDe(a)} title="Registro de entrega-recepción firmado por el trabajador"
                                style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${color}55`, color, borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {est.estado === 'firmado' ? '✓ Entrega firmada' : est.estado === 'en_firma' ? est.texto : 'Firmar entrega'}
                              </button>
                            );
                          })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {firmasOk && tab === 'asignaciones' && (
        <RegistrosConservados companyId={companyId} tipo="epp_entrega" usuarioId={profile?.id ?? null} puedeEditar={canEdit} esAdmin={isAdmin} recarga={datosFirmas} />
      )}

      {/* Modals */}
      {firmaDe && (() => {
        const reg = registroDeAsignacion(firmaDe.id);
        const grupo = grupoDeEntrega(firmaDe);
        const w = workerMap.get(firmaDe.worker_id);
        return (
          <RegistroFirmasModal
            titulo={`${w?.full_name ?? 'Trabajador'} · entrega del ${fmtDate(firmaDe.assigned_date)}`}
            companyId={companyId}
            usuarioId={profile?.id ?? null}
            registro={reg}
            firmas={reg ? datosFirmas.firmas.filter(f => f.record_id === reg.id) : []}
            origen={{ worker_id: firmaDe.worker_id, asignaciones: grupo.map(g => g.id) }}
            devoluciones={Object.fromEntries(grupo.map(g => [g.id, g.returned ? g.returned_date : null]))}
            armarContenido={async () => {
              if (!w) throw new Error('El trabajador no está activo: no se puede firmar su entrega.');
              const contenido = contenidoDeEntrega(w, firmaDe.assigned_date, grupo.map(g => {
                const e = eppMap.get(g.epp_id);
                return { id: g.id, epp: e?.name ?? 'EPP', categoria: e ? EPP_CATEGORY_LABELS[e.category] ?? e.category : null, marca: e?.brand ?? null,
                  modelo: e?.model ?? null, condicion: CONDITION_LABELS[g.condition] ?? g.condition, assigned_date: g.assigned_date, expiry_date: g.expiry_date };
              }), displayCompany?.name ?? '');
              return { contenido, faltantes: faltantesContenido(contenido) };
            }}
            puedeEditar={canEdit}
            esAdmin={isAdmin}
            onCambio={loadData}
            onCerrar={() => setFirmaDe(null)}
          />
        );
      })()}
      {catalogModal !== undefined && (
        <CatalogModal
          item={catalogModal}
          companyId={companyId}
          onClose={() => setCatalogModal(undefined)}
          onSaved={handleSaved}
        />
      )}
      {assignModal !== undefined && (
        <AssignModal
          assignment={assignModal}
          workers={workers}
          catalog={catalog}
          companyId={companyId}
          onClose={() => setAssignModal(undefined)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ msg }: { msg: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#475569', fontSize: '0.88rem' }}>
      {msg}
    </div>
  );
}
